/**
 * Upgrades the product-tabs rail from links into tabs.
 *
 * Before this runs the pills are ordinary anchors to each collection and only the first
 * panel is visible, so the section is usable without JavaScript — a shopper navigates
 * instead of switching in place. That is also why the ARIA roles are applied here rather
 * than in Liquid: announcing "tab" while the control still navigates would describe
 * behaviour the page does not have yet.
 *
 * Implements the WAI-ARIA tabs pattern: one tab in the tab order at a time (roving
 * tabindex), arrow keys move between tabs, Home/End jump to the ends.
 */
class CrewcupProductTabs extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLAnchorElement[]} */
    this.tabs = [...this.querySelectorAll('.cc-tabs__pill')];
    /** @type {HTMLElement[]} */
    this.panels = [...this.querySelectorAll('.cc-tabs__panel')];

    // A single tab is not a tab set — leave it as a plain link.
    if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

    const rail = this.querySelector('.cc-tabs__rail');
    rail?.setAttribute('role', 'tablist');

    for (const [i, tab] of this.tabs.entries()) {
      const panel = this.panels[i];
      if (!panel) continue;

      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      // Panels hold their own focusable controls, so they do not need to be focusable
      // themselves; they do need a label, which aria-labelledby above provides.
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.#select(i, { focus: false });
      });
      tab.addEventListener('keydown', this.#onKeydown);
    }

    this.#select(0, { focus: false });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const current = this.tabs.indexOf(/** @type {HTMLAnchorElement} */ (event.currentTarget));
    if (current === -1) return;

    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next, { focus: true });
  };

  /**
   * @param {number} index
   * @param {{ focus: boolean }} options
   */
  #select(index, { focus }) {
    for (const [i, tab] of this.tabs.entries()) {
      const active = i === index;
      const panel = this.panels[i];

      tab.setAttribute('aria-selected', String(active));
      // Roving tabindex: Tab moves past the whole set, arrows move within it.
      tab.setAttribute('tabindex', active ? '0' : '-1');
      if (active) tab.dataset.current = 'true';
      else delete tab.dataset.current;

      if (panel) panel.hidden = !active;
    }

    const tab = this.tabs[index];
    // preventScroll, then reveal inside the rail ourselves. A bare focus() scrolls the
    // page to bring the element into view, which on arrow-key navigation jumped the
    // viewport away from whatever the visitor was reading.
    if (focus) tab?.focus({ preventScroll: true });

    this.#revealInRail(tab);
  }

  /**
   * Brings a pill into view by scrolling THE RAIL, never the document.
   *
   * This replaced scrollIntoView({ block: 'nearest' }). That call was made on connect as
   * well as on click, and 'nearest' does not mean "do not scroll the page" — it means
   * "scroll the least amount needed". With the section below the fold on page load, the
   * least amount needed was to scroll the whole homepage down to the tabs, which is
   * exactly what visitors saw on every refresh.
   *
   * Setting the rail's own scrollLeft cannot move the document, whatever is on screen.
   *
   * @param {HTMLElement | undefined} tab
   */
  #revealInRail(tab) {
    const rail = this.querySelector('.cc-tabs__rail');
    if (!(rail instanceof HTMLElement) || !tab) return;

    const railBox = rail.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();

    if (tabBox.left < railBox.left) {
      rail.scrollLeft -= railBox.left - tabBox.left;
    } else if (tabBox.right > railBox.right) {
      rail.scrollLeft += tabBox.right - railBox.right;
    }
  }
}

if (!customElements.get('crewcup-product-tabs')) {
  customElements.define('crewcup-product-tabs', CrewcupProductTabs);
}
